import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Plus, Search, Tags, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import type { Attribute } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Input } from '@nucleus-commerce/ui/components/input';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { hasValues } from '../api/attribute-mappers';
import { AttributeDeleteDialog } from '../components/attribute-delete-dialog';
import { AttributeDialog } from '../components/attribute-dialog';
import { AttributeEditor } from '../components/attribute-editor';
import { useAttributes } from '../hooks/use-attributes';

const LIST_PATH = '/org/catalog-setup/attributes/' as const;

function Loading() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12" />
      ))}
    </div>
  );
}

/** Body of the Attributes tab's list; the catalog setup layout supplies the page header. */
function AttributeList() {
  const { t } = useTranslation('attributes');
  const query = useAttributes();
  const navigate = useNavigate({ from: LIST_PATH });
  // Not strict so the page also renders under a test router; the list route validates `q`.
  const search: { q?: unknown } = useSearch({ strict: false });
  const [q, setQ] = useState(typeof search.q === 'string' ? search.q : '');
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<Attribute | null>(null);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const all = query.data ?? [];
    return needle ? all.filter((row) => row.label.toLowerCase().includes(needle)) : all;
  }, [query.data, q]);

  const onSearch = (next: string) => {
    setQ(next);
    void navigate({ search: (prev) => ({ ...prev, q: next || undefined }), replace: true });
  };

  // The tour points at the toolbar button; the empty state's copy of it carries no anchor.
  const newButton = (tour?: string) => (
    <Can permission="attributes.write">
      <Button onClick={() => setCreating(true)} data-tour={tour}>
        <Plus aria-hidden />
        {t('new')}
      </Button>
    </Can>
  );
  // The tour explains the badges on the first row that shows any.
  const badgedRowId = rows.find((row) => row.usableAsOption || row.isFilterable)?.id;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-muted-foreground max-w-prose text-sm">{t('description')}</p>
        {newButton('dictionary.attributes.new-button')}
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !query.data ? (
        <Loading />
      ) : query.data.length === 0 ? (
        <EmptyState
          icon={Tags}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton()}
          tour="dictionary.attributes.empty"
          tourLink
        />
      ) : (
        <>
          <div className="relative max-w-sm">
            <Search
              aria-hidden
              className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
            />
            <Input
              type="search"
              aria-label={t('search')}
              placeholder={t('search')}
              className="pl-8"
              value={q}
              onChange={(e) => onSearch(e.target.value)}
            />
          </div>
          {rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('noMatches', { q: q.trim() })}</p>
          ) : (
            <Card className="py-0" data-tour="dictionary.attributes.table">
              <CardContent className="px-0">
                <Table aria-label={t('title')}>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="pl-6">{t('columns.attribute')}</TableHead>
                      <TableHead>{t('columns.dataType')}</TableHead>
                      <TableHead className="text-right">{t('columns.values')}</TableHead>
                      <TableHead className="pr-6 text-right">{t('columns.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row) => (
                      <TableRow key={row.id} aria-label={row.label}>
                        <TableCell className="pl-6 align-top">
                          <div className="flex min-w-0 flex-col gap-1">
                            <Link
                              to="/org/catalog-setup/attributes/$attributeId"
                              params={{ attributeId: row.id }}
                              aria-label={t('actions.edit', { label: row.label })}
                              className="w-fit font-medium hover:underline"
                            >
                              {row.label}
                            </Link>
                            <code className="text-muted-foreground text-xs">{row.key}</code>
                            {row.usableAsOption || row.isFilterable ? (
                              <div
                                className="flex flex-wrap gap-1"
                                data-tour={
                                  row.id === badgedRowId ? 'dictionary.attributes.flags' : undefined
                                }
                              >
                                {row.usableAsOption ? (
                                  <Badge variant="outline" className="font-normal">
                                    {t('badges.option')}
                                  </Badge>
                                ) : null}
                                {row.isFilterable ? (
                                  <Badge variant="outline" className="font-normal">
                                    {t('badges.filter')}
                                  </Badge>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="align-top">
                          <Badge variant="secondary" className="font-normal">
                            {t(`dataType.${row.dataType}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right align-top text-sm tabular-nums">
                          {hasValues(row.dataType)
                            ? t('valueCount', { count: row.values.length })
                            : '—'}
                        </TableCell>
                        <TableCell className="pr-6 text-right align-top">
                          <Can permission="attributes.write">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              className="text-destructive hover:text-destructive"
                              aria-label={t('actions.delete', { label: row.label })}
                              onClick={() => setDeleting(row)}
                            >
                              <Trash2 aria-hidden />
                            </Button>
                          </Can>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </>
      )}
      <AttributeDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(attribute) =>
          void navigate({
            to: '/org/catalog-setup/attributes/$attributeId',
            params: { attributeId: attribute.id },
          })
        }
      />
      <AttributeDeleteDialog
        attribute={deleting}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      />
    </div>
  );
}

/** The attribute comes out of the cached list, the same copy the product editor reads. */
function AttributeDetail({ attributeId }: { attributeId: string }) {
  const { t } = useTranslation('attributes');
  const query = useAttributes();
  const attribute = query.data?.find((row) => row.id === attributeId);

  if (query.error) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  if (!query.data) return <Loading />;
  if (!attribute) {
    return (
      <EmptyState
        icon={Tags}
        title={t('notFound.title')}
        description={t('notFound.description')}
        action={
          <Button asChild variant="outline">
            <Link to="/org/catalog-setup/attributes">{t('back')}</Link>
          </Button>
        }
      />
    );
  }
  return <AttributeEditor key={attribute.updatedAt} attribute={attribute} />;
}

/**
 * The Attributes tab: the list, or one attribute's editor when the route carries an
 * `attributeId` (`catalog-setup/attributes/$attributeId`).
 */
export function AttributesPage() {
  // Not strict so the page also renders under a test router; only the detail route has the param.
  const { attributeId } = useParams({ strict: false });
  return (
    <FeatureErrorBoundary name="dictionary.attributes">
      {attributeId ? <AttributeDetail attributeId={attributeId} /> : <AttributeList />}
    </FeatureErrorBoundary>
  );
}
